import { useState, useCallback } from "react";
import { SimCanvas } from "@/components/SimCanvas";
import { createBall, DEFAULT_CONFIG } from "@/lib/physics";
import type { Ball, PhysicsConfig } from "@/lib/physics";
import { Button } from "@/components/ui/button";

export function Lesson3Collisions() {
	const [balls, setBalls] = useState<Ball[]>(() => makeCollisionDemo());
	const [config] = useState<PhysicsConfig>({
		...DEFAULT_CONFIG,
		restitution: 0.95,
		gravity: 500,
	});
	const [showVectors, setShowVectors] = useState(true);

	function makeCollisionDemo(): Ball[] {
		return [
			createBall(150, 200, 300, 0, 30),
			createBall(550, 200, -300, 0, 30),
			createBall(350, 100, 0, 200, 20),
		];
	}

	function makeMassDemo(): Ball[] {
		return [
			createBall(150, 200, 250, 0, 15),
			createBall(500, 200, -50, 0, 45),
		];
	}

	function makeNewtonsCradle(): Ball[] {
		const balls: Ball[] = [];
		const startX = 250;
		const y = 200;
		const r = 20;
		for (let i = 0; i < 5; i++) {
			balls.push(createBall(startX + i * (r * 2 + 2), y, 0, 0, r));
		}
		// Give first ball velocity
		balls[0].vel.x = 400;
		return balls;
	}

	const handleClick = useCallback((x: number, y: number) => {
		const vx = (Math.random() - 0.5) * 600;
		const vy = (Math.random() - 0.5) * 400;
		setBalls((prev) => [...prev, createBall(x, y, vx, vy, 15 + Math.random() * 20)]);
	}, []);

	return (
		<div className="space-y-4">
			<div className="flex flex-wrap gap-2">
				<Button variant="outline" size="sm" onClick={() => setBalls(makeCollisionDemo())}>
					Head-On
				</Button>
				<Button variant="outline" size="sm" onClick={() => setBalls(makeMassDemo())}>
					Mass Difference
				</Button>
				<Button variant="outline" size="sm" onClick={() => setBalls(makeNewtonsCradle())}>
					Newton's Cradle
				</Button>
				<Button
					variant="outline"
					size="sm"
					onClick={() => setShowVectors(!showVectors)}
					className={showVectors ? "border-cyan-400 text-cyan-400" : ""}
				>
					{showVectors ? "Hide" : "Show"} Velocity
				</Button>
			</div>

			<SimCanvas
				balls={balls}
				config={config}
				showTrails
				showVectors={showVectors}
				showEnergy
				onClick={handleClick}
			/>

			<p className="text-sm text-muted-foreground">
				<strong>Conservation of Momentum:</strong> In every collision, total momentum is preserved. Notice how when a small ball hits a big one, the small ball bounces back while the big one barely moves. White arrows show velocity direction and magnitude.
			</p>
		</div>
	);
}
